import React from 'react';
import { LogOut, Maximize2, Menu, Monitor, Play, Smartphone, Square } from 'lucide-react';
import SyncStatus from './SyncStatus';

/** The top bar: menu toggle, brand, LIVE switch, Open Display / Stage windows, sync status and Logout. */
const AppHeader = ({
  displayConnected,
  dm,
  isLive,
  liveStatus,
  onLogout,
  openDisplayWindow,
  openStageWindow,
  onOpenRemote,
  remoteConnected,
  setIsLive,
  setSidebarOpen,
  sidebarOpen,
  stageConnected,
  t,
}) => {
  return (
    <div className={`relative shrink-0 border-b px-3 sm:px-5 py-2 min-h-16 flex items-center shadow-sm z-30 ${t.headerBg}`}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 w-full">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            title={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
            className={`p-2 rounded-lg transition-all ${t.dropdownItem}`}
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#001023] shrink-0">
            <img
              src={`${process.env.PUBLIC_URL}/logo512.png`}
              alt="SleekHear"
              className="w-full h-[155%] object-cover object-top"
            />
          </div>
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${t.textPrimary}`}>
              SleekHear
            </h1>
            <p className={`hidden sm:block text-xs ${t.textSecondary}`}>Live Presentation Console</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          <button
            onClick={() => setIsLive(!isLive)}
            className={`flex items-center gap-2 px-3 sm:px-5 py-2.5 rounded-lg font-bold shadow-lg transition-all hover:scale-105 ${
              isLive
                ? 'bg-red-600 text-slate-50 hover:bg-red-600/90 animate-pulse'
                : dm ? 'bg-slate-700 text-slate-50 hover:bg-slate-600' : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            {isLive ? <Play className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {isLive ? 'LIVE' : 'OFF AIR'}
          </button>
          {liveStatus && (
            <span
              data-testid="live-status"
              title={liveStatus}
              className={`hidden xl:block max-w-[16rem] truncate text-sm font-semibold ${isLive ? 'text-red-400' : t.textSecondary}`}
            >
              {liveStatus}
            </span>
          )}
          <button
            onClick={openDisplayWindow}
            title="Open the projector display"
            aria-label="Open Display"
            className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-brand-600 text-slate-50 rounded-lg hover:bg-brand-500 shadow-lg font-bold transition-all hover:scale-105"
          >
            <Maximize2 className="w-4 h-4" />
            <span className="hidden md:inline">Open Display</span>
            <span
              className={`w-2 h-2 rounded-full ${displayConnected ? 'bg-green-400' : 'bg-slate-400'}`}
              title={displayConnected ? 'Display connected' : 'Display not connected'}
            />
          </button>
          <button
            onClick={openStageWindow}
            title="Open the stage screen (current slide, next item and clock) for the preacher and band"
            aria-label="Open Stage screen"
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg shadow-lg font-semibold transition-all hover:scale-105 ${dm ? 'bg-slate-700 text-slate-50 hover:bg-slate-600' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
          >
            <Monitor className="w-4 h-4" />
            <span className="hidden md:inline">Stage</span>
            <span
              className={`w-2 h-2 rounded-full ${stageConnected ? 'bg-green-400' : 'bg-slate-400'}`}
              title={stageConnected ? 'Stage screen connected' : 'Stage screen not connected'}
            />
          </button>
          <button
            onClick={onOpenRemote}
            title="Control the run-sheet from a phone"
            aria-label="Phone remote"
            className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg shadow-lg font-semibold transition-all hover:scale-105 ${dm ? 'bg-slate-700 text-slate-50 hover:bg-slate-600' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
          >
            <Smartphone className="w-4 h-4" />
            <span className="hidden md:inline">Remote</span>
            <span
              className={`w-2 h-2 rounded-full ${remoteConnected ? 'bg-green-400' : 'bg-slate-400'}`}
              title={remoteConnected ? 'Phone connected' : 'No phone connected'}
            />
          </button>
          <SyncStatus dm={dm} />
          {onLogout && (
            <button
              onClick={onLogout}
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-slate-700 text-slate-50 rounded-lg hover:bg-slate-600 shadow-lg font-semibold transition-all hover:scale-105"
              title="Logout"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppHeader;
